/** ITCSI platform: advisor firms and their first partner (M14). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { shortDate } from '../../lib/format';
import { Alert, Button, Card, EmptyState, Field, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';
import { SecretReveal, errorText } from '../integrations/shared';

interface Firm { id: string; name: string; code: string; tin: string | null; active: boolean; members: number; clients: number; createdAt: string }

export function FirmsPage(): JSX.Element {
  const qc = useQueryClient();
  const firms = useQuery({ queryKey: ['platform-firms'], queryFn: () => api<Firm[]>('/platform/firms') });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [tin, setTin] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [result, setResult] = useState<{ code: string; partnerEmail: string; partnerTemporaryPassword: string | null } | null>(null);
  const create = useMutation({
    mutationFn: () => api<{ code: string; partnerEmail: string; partnerTemporaryPassword: string | null }>('/platform/firms', { method: 'POST', body: { name, tin: tin || undefined, partnerName, partnerEmail } }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['platform-firms'] }); setCreating(false); setResult(r); setName(''); setTin(''); setPartnerName(''); setPartnerEmail(''); },
  });
  return (
    <>
      <PageHeader title="Advisor Firms" description="Accounting firms that serve many clients. A firm sees no company until that company's owner connects it." actions={<Button onClick={() => setCreating(true)}><Plus aria-hidden="true" className="h-4 w-4" /> New firm</Button>} />
      <Card>
        {firms.isLoading && <Skeleton className="h-32 w-full" />}
        {firms.data?.length === 0 && <EmptyState title="No advisor firms yet" />}
        {firms.data && firms.data.length > 0 && (
          <TableWrap>
            <thead><tr><Th>Firm</Th><Th>Code</Th><Th align="right">Team</Th><Th align="right">Clients</Th><Th>Status</Th><Th>Created</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {firms.data.map((f) => (
                <tr key={f.id}>
                  <Td className="font-medium">{f.name}</Td>
                  <Td><code className="text-xs">{f.code}</code></Td>
                  <Td align="right" className="tabular-nums">{f.members}</Td>
                  <Td align="right" className="tabular-nums">{f.clients}</Td>
                  <Td><StatusChip tone={f.active ? 'ok' : 'critical'}>{f.active ? 'Active' : 'Suspended'}</StatusChip></Td>
                  <Td className="text-slate-500">{shortDate(f.createdAt)}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
      {creating && (
        <Modal title="New advisor firm" description="The partner manages the firm's team and gives clients the firm code." onClose={() => setCreating(false)}>
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            {create.error && <Alert tone="critical">{errorText(create.error)}</Alert>}
            <Field label="Firm name" required><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required minLength={2} /></Field>
            <Field label="TIN"><input className={inputClass} value={tin} onChange={(e) => setTin(e.target.value)} placeholder="000-000-000" /></Field>
            <Field label="Partner name" required><input className={inputClass} value={partnerName} onChange={(e) => setPartnerName(e.target.value)} required minLength={2} /></Field>
            <Field label="Partner email" required><input className={inputClass} type="email" value={partnerEmail} onChange={(e) => setPartnerEmail(e.target.value)} required /></Field>
            <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setCreating(false)}>Cancel</Button><Button type="submit" busy={create.isPending}>Create firm</Button></div>
          </form>
        </Modal>
      )}
      {result && <SecretReveal title="Firm created" items={[{ label: 'Firm code (give to clients)', value: result.code }, { label: 'Partner email', value: result.partnerEmail }, ...(result.partnerTemporaryPassword ? [{ label: 'Temporary password', value: result.partnerTemporaryPassword }] : [])]} onClose={() => setResult(null)} />}
    </>
  );
}
